import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';

import { logout } from '../../api/auth';
import { clearSession } from '../../api/session-store';
import { useSession } from '../../hooks/use-session';
import { useLocalStorageState } from '../../lib/use-local-storage-state';
import { cn } from '../../lib/cn';
import { wsClient } from '../../ws/client';
import { DEV_NAV_ITEMS, isDevToolsEnabled, NAV_ITEMS, visibleNavItems, type NavItem } from './nav';

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 rounded px-3 py-2 text-sm font-medium text-fg hover:bg-muted',
          collapsed && 'justify-center px-0',
          isActive && 'bg-primary text-primary-fg hover:bg-primary',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  );
}

export function AppLayout() {
  const { me } = useSession();
  const items = visibleNavItems(NAV_ITEMS, me?.role);
  const [collapsed, setCollapsed] = useLocalStorageState('od.nav.collapsed', false);

  async function handleLogout() {
    try {
      await logout();
    } finally {
      wsClient.close();
      clearSession();
      window.location.assign('/login');
    }
  }

  return (
    // Was `min-w-[1024px]` — that forced every route to render at desktop width even when
    // its own layout is responsive (e.g. the inbox's single-pane mode below `lg`), which is
    // what produced the 660–1000px squish: fixed-width panels (420px list + 288px contact
    // card) fought over the little space left once the app got clamped to 1024px inside a
    // narrower viewport. Routes now own their own responsive behavior.
    <div className="flex h-full min-w-[360px]">
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-border bg-muted/40 shadow-[1px_0_0_0_rgba(15,23,42,0.03)] transition-[width] duration-150',
          collapsed ? 'w-16' : 'w-56',
        )}
      >
        <div className={cn('flex items-center gap-2 px-4 py-4', collapsed && 'justify-center px-2')}>
          {!collapsed && <span className="text-lg font-semibold">Outreach Desk</span>}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            className={cn('ml-auto rounded p-1 text-muted-fg hover:bg-muted hover:text-fg', collapsed && 'ml-0')}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
        <nav className="flex flex-col gap-1 px-2">
          {items.map((item) => (
            <NavRow key={item.to} item={item} collapsed={collapsed} />
          ))}
          {isDevToolsEnabled() && (
            <>
              {!collapsed && <div className="mt-2 px-3 text-xs font-semibold uppercase text-muted-fg">Dev</div>}
              {DEV_NAV_ITEMS.map((item) => (
                <NavRow key={item.to} item={item} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>
        <div className={cn('mt-auto border-t border-border px-4 py-3 text-sm', collapsed && 'px-2 text-center')}>
          {!collapsed && (
            <>
              <div className="truncate font-medium">{me?.user.name ?? me?.user.email}</div>
              <div className="truncate text-xs text-muted-fg">{me?.workspace.name}</div>
            </>
          )}
          <button
            onClick={() => void handleLogout()}
            title="Выйти"
            className={cn(
              'mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline',
              collapsed && 'mt-0 justify-center',
            )}
          >
            <LogOut className="h-3.5 w-3.5" />
            {!collapsed && 'Выйти'}
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
